import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { automationRulesService } from '../../../services/automationRulesService';
import { emailTemplateService } from '../../../services/emailTemplateService';
import ConditionBuilder from './ConditionBuilder';
import { trackCampaignGoal, trackUserBehaviorFunnel } from '../../../utils/analytics';

const RuleBuilder = ({ rule, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    ruleName: '',
    description: '',
    triggerType: 'client_revenue_threshold',
    emailTemplateId: '',
    status: 'draft',
    priority: 0
  });
  const [conditions, setConditions] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');

  useEffect(() => {
    loadTemplates();
    if (rule) {
      setFormData({
        ruleName: rule?.ruleName || '',
        description: rule?.description || '',
        triggerType: rule?.triggerType || 'client_revenue_threshold',
        emailTemplateId: rule?.emailTemplateId || '',
        status: rule?.status || 'draft',
        priority: rule?.priority || 0
      });
      setConditions(rule?.conditions || []);
    }
  }, [rule]);

  const loadTemplates = async () => {
    try {
      const data = await emailTemplateService?.getAllTemplates({ status: 'active' });
      setTemplates(data || []);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const triggerTypeOptions = [
    { value: 'client_revenue_threshold', label: 'Client Revenue Threshold' },
    { value: 'service_frequency', label: 'Service Frequency' },
    { value: 'visit_count_milestone', label: 'Visit Count Milestone' },
    { value: 'membership_anniversary', label: 'Membership Anniversary' },
    { value: 'invoice_amount', label: 'Invoice Amount' },
    { value: 'payment_status', label: 'Payment Status' },
    { value: 'client_lifetime_value', label: 'Client Lifetime Value' },
    { value: 'service_interval', label: 'Service Interval' },
    { value: 'feedback_rating_threshold', label: 'Feedback Rating Threshold' },
    { value: 'service_issue_detection', label: 'Service Issue Detection' }
  ];

  const statusOptions = [
    { value: 'draft', label: 'Draft' },
    { value: 'active', label: 'Active' },
    { value: 'paused', label: 'Paused' },
    { value: 'archived', label: 'Archived' }
  ];

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!formData?.ruleName?.trim()) {
      alert('Please enter a rule name');
      return;
    }

    if (conditions?.length === 0) {
      alert('Please add at least one condition');
      return;
    }

    try {
      setLoading(true);
      
      if (rule) {
        await automationRulesService?.updateRule(rule?.id, formData);
        await automationRulesService?.updateConditions(rule?.id, conditions);
        
        // Track rule update
        trackCampaignGoal('automation_rule_updated', {
          goal_type: 'rule_modification',
          rule_id: rule?.id,
          rule_name: formData?.ruleName,
          trigger_type: formData?.triggerType,
          condition_count: conditions?.length,
          status: formData?.status
        });
      } else {
        const newRule = await automationRulesService?.createRule(formData, conditions);
        
        // Track rule creation goal
        trackCampaignGoal('automation_rule_created', {
          goal_type: 'rule_creation',
          rule_id: newRule?.id,
          rule_name: formData?.ruleName,
          trigger_type: formData?.triggerType,
          condition_count: conditions?.length,
          status: formData?.status,
          email_template_id: formData?.emailTemplateId
        });
        
        // Track funnel completion
        trackUserBehaviorFunnel('automation_workflow_created', {
          funnel_stage: 'workflow_complete',
          rule_name: formData?.ruleName,
          trigger_type: formData?.triggerType,
          condition_count: conditions?.length
        });
      }
      
      onSave();
    } catch (error) {
      console.error('Error saving rule:', error);
      alert('Failed to save automation rule');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg">
      <div className="border-b border-border p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-heading font-bold text-foreground">
            {rule ? 'Edit Automation Rule' : 'Create New Automation Rule'}
          </h2>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-muted rounded-lg transition-smooth"
          >
            <Icon name="X" size={20} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <div className="flex gap-1 px-6">
          <button
            onClick={() => setActiveTab('basic')}
            className={`px-4 py-3 font-medium transition-smooth ${
              activeTab === 'basic' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            Basic Info
          </button>
          <button
            onClick={() => setActiveTab('conditions')}
            className={`px-4 py-3 font-medium transition-smooth ${
              activeTab === 'conditions' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            Conditions ({conditions?.length})
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="p-6">
          {activeTab === 'basic' && (
            <div className="space-y-6">
              <Input
                label="Rule Name"
                required
                value={formData?.ruleName}
                onChange={(e) => setFormData({ ...formData, ruleName: e?.target?.value })}
                placeholder="e.g., High-Value Client Revenue Alert"
              />

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Description
                </label>
                <textarea
                  value={formData?.description}
                  onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
                  placeholder="Describe what this rule does..."
                  rows={3}
                  className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <Select
                label="Trigger Type"
                required
                options={triggerTypeOptions}
                value={formData?.triggerType}
                onChange={(value) => setFormData({ ...formData, triggerType: value })}
              />

              <Select
                label="Email Template"
                required
                options={templates?.map(t => ({
                  value: t?.id,
                  label: t?.templateName,
                  description: t?.segment
                }))}
                value={formData?.emailTemplateId}
                onChange={(value) => setFormData({ ...formData, emailTemplateId: value })}
                placeholder="Select email template"
              />

              <div className="grid grid-cols-2 gap-4">
                <Select
                  label="Status"
                  options={statusOptions}
                  value={formData?.status}
                  onChange={(value) => setFormData({ ...formData, status: value })}
                />

                <Input
                  label="Priority"
                  type="number"
                  value={formData?.priority}
                  onChange={(e) => setFormData({ ...formData, priority: parseInt(e?.target?.value) || 0 })}
                  description="Higher priority rules execute first"
                />
              </div>
            </div>
          )}

          {activeTab === 'conditions' && (
            <ConditionBuilder
              triggerType={formData?.triggerType}
              conditions={conditions}
              onChange={setConditions}
            />
          )}
        </div>

        {/* Footer Actions */}
        <div className="border-t border-border p-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-smooth"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth disabled:opacity-50 flex items-center gap-2"
          >
            {loading && <Icon name="Loader2" size={16} className="animate-spin" />}
            <span>{rule ? 'Update Rule' : 'Create Rule'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default RuleBuilder;